import { Pressable as Slot_Pressable } from '@rn-primitives/slot'
import { Link, type Href } from 'expo-router'
import { type ReactNode } from 'react'
import { Pressable, Switch, View, type GestureResponderEvent } from 'react-native'
import Animated from 'react-native-reanimated'
import Icon, { type IconLibrary, type IconName } from '~/components/Icon.tsx'
import { Button, ButtonLabel, type ButtonSize, type ButtonVariant } from '~/components/ui/button'
import { Text } from '~/components/ui/text.tsx'
import designTokens from '~/lib/designTokens.ts'
import { cn } from '~/lib/utils.ts'

export type ActionType = 'add' | 'remove' | 'toggle' | 'navigate' | 'set'

type OnPressAction = (
  event: GestureResponderEvent | React.MouseEvent<HTMLAnchorElement, MouseEvent>
) => unknown

export type Action = {
  key: string
  label?: string | ReactNode
  description?: string
  icon?: IconName
  iconLibrary?: IconLibrary
  iconSize?: number
  iconColor?: string
  onPress?: OnPressAction
  href?: Href
  disabled?: boolean
  className?: string
  iconClassName?: string
  contentClassName?: string
  labelClassName?: string
  descriptionClassName?: string
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  isActive?: boolean
  theme?: string
  isWithinLink?: boolean
  actionType?: ActionType
  actionIconClassName?: string
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

export function ActionButton({
  action: actionProps,
  className: _className,
  onPress: secondaryOnPress,
}: {
  action: Action
  className?: string
  onPress?: OnPressAction
}) {
  const {
    label,
    description,
    icon,
    iconLibrary,
    iconSize,
    iconColor,
    onPress,
    href,
    disabled,
    className = _className,
    iconClassName,
    contentClassName,
    labelClassName,
    descriptionClassName,
    buttonVariant,
    buttonSize,
    theme,
    actionType = 'navigate',
    isActive,
    isWithinLink,
    actionIconClassName,
  } = actionProps

  const handlePress = (
    event: GestureResponderEvent | React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault()
    onPress?.(event)
    secondaryOnPress?.(event)
  }

  const isSmall = buttonSize === 'sm' || buttonSize === 'xs'

  if (description) {
    const iconElement = icon && (
      <View
        className={cn(
          'relative size-10 items-center justify-center',
          isSmall && '-ml-1 -mt-0.5 h-8 w-auto items-center'
        )}>
        {theme && (
          <Icon
            name={icon.replace('-outline', '') as IconName}
            size={isSmall ? 20 : 29}
            library={iconLibrary as undefined}
            className={cn('absolute left-[4px] top-[4px]')}
            color={theme}
          />
        )}
        <Icon
          name={icon}
          size={isSmall ? 20 : 28}
          library={iconLibrary as undefined}
          className={cn('shrink-0')}
          color={designTokens.primary}
        />
      </View>
    )

    const labelElement =
      typeof label === 'string' ? (
        <Text
          className={cn(
            'sm:font-bold! select-none whitespace-nowrap font-semibold text-xl sm:whitespace-normal sm:text-2xl',
            buttonSize === 'sm' && 'text-xl',
            labelClassName
          )}>
          {label}
        </Text>
      ) : (
        label
      )

    const descriptionElement = (
      <View className={cn('pl-13 w-full pr-10', isSmall && 'pl-0 pr-0')}>
        <Text
          className={cn(
            'select-none text-pretty font-light text-sm/normal text-foreground sm:text-base/normal',
            descriptionClassName
          )}>
          {description}
        </Text>
      </View>
    )

    const content = (
      <AnimatedPressable
        className={cn(
          'flex-row flex-wrap gap-3 border-b-2 border-[#D1D6F0] px-6 py-8 transition-all duration-150 hover:bg-muted active:scale-[0.985] active:bg-muted',
          disabled && 'pointer-events-none opacity-50',
          isSmall && 'gap-1 sm:gap-2',
          _className,
          className
        )}>
        <View
          className={cn(
            'w-full flex-row items-center gap-3',
            isSmall && 'items-start',
            contentClassName
          )}>
          {iconElement}
          {labelElement}
          {actionType !== 'toggle' && !disabled && !isSmall ? (
            <View
              className={cn(
                '-my-2 ml-auto size-14 items-center justify-center rounded-full bg-background',
                actionIconClassName
              )}>
              <Icon
                name={
                  actionType === 'add'
                    ? 'plus'
                    : actionType === 'remove'
                      ? 'minus'
                      : actionType === 'set'
                        ? 'checkbox-marked-outline'
                        : 'arrow-right'
                }
                size={30}
                color={designTokens.accent}
              />
            </View>
          ) : !disabled && !isSmall ? (
            <Switch value={isActive} pointerEvents="none" className="pointer-events-none ml-auto" />
          ) : null}
        </View>
        {descriptionElement}
      </AnimatedPressable>
    )

    if (href && !isWithinLink) {
      return (
        <Link href={href as Href} asChild onPress={secondaryOnPress}>
          {content}
        </Link>
      )
    }

    return <Slot_Pressable onPress={handlePress}>{content}</Slot_Pressable>
  }

  const content = (
    <Button
      asChild={false} // Must be set false, otherwise the asChild prop from the wrapper element is propagated to the button, causing it to also act as asChild.
      variant={buttonVariant}
      size={buttonSize}
      disabled={disabled}
      accessory={
        actionType === 'toggle' && buttonSize !== 'icon' ? (
          <Switch
            disabled={disabled}
            value={isActive}
            // eslint-disable-next-line i18next/no-literal-string
            pointerEvents="none"
            className="pointer-events-none"
          />
        ) : undefined
      }
      className={cn(icon && 'flex-row gap-3', buttonSize === 'icon' && 'p-0', className)}>
      {icon && (
        <Icon
          name={icon}
          library={iconLibrary as undefined}
          size={iconSize}
          className={iconClassName}
          color={iconColor}
        />
      )}
      {label && buttonSize !== 'icon' && (
        <ButtonLabel className={labelClassName}>{label}</ButtonLabel>
      )}
    </Button>
  )

  if (href && !isWithinLink) {
    return (
      <Link href={href as Href} asChild>
        {content}
      </Link>
    )
  }

  return <Slot_Pressable onPress={handlePress}>{content}</Slot_Pressable>
}
